/**
 * /hypnose — Landing pilier de la rubrique.
 * Objectif : présenter le socle éditorial (approche + 4 pages enfant),
 * intercepter les recherches "hypnose Pantin", "hypnothérapeute Pantin",
 * et rediriger sémantiquement vers les pages ciblées.
 */
import React from "react";
import { Seo } from "../../components/Layout";
import { PageHero, InternalLinkCard, EndCTA } from "../../components/PageParts";

const BREADCRUMB = [{ to: "/", label: "Accueil" }, { label: "L'Hypnose" }];

export default function HypnoseLanding() {
  return (
    <>
      <Seo
        title="L'Hypnose — Hypnothérapeute à Pantin"
        description="Hypnose ericksonienne et hypnose spirituelle avec Valentine DL. Cabinet à Pantin (Métro Église de Pantin, 15 min de Paris) et séances en ligne. Comprendre les différentes approches et choisir la vôtre."
        canonical="https://www.parfaitsimparfaits.fr/hypnose"
      />

      <PageHero
        breadcrumb={BREADCRUMB}
        eyebrow="L'Hypnose"
        title="Deux approches, une même exigence :"
        italic="votre mieux-être, en confiance."
        subtitle="Praticienne à Pantin, à 15 min de Paris, et en ligne, j'accompagne celles et ceux qui souhaitent avancer, dénouer un blocage ou prendre du recul — avec discernement, dans un cadre professionnel et rassurant."
      />

      <section className="px-6 lg:px-10 pb-16" data-testid="hypnose-intro">
        <div className="max-w-3xl mx-auto prose-mystic text-lg text-sand-700 leading-relaxed space-y-5">
          <p>
            L'hypnose est un <strong className="text-sand-900 font-medium">état naturel de conscience</strong>,
            comparable à ces moments où l'esprit s'absorbe dans une lecture ou une rêverie. Utilisée de manière
            professionnelle, elle permet de mobiliser les ressources de l'inconscient pour opérer des changements
            durables — sans jamais faire perdre le contrôle.
          </p>
          <p>
            Je propose deux approches complémentaires. L'<strong className="text-sand-900 font-medium">hypnose
            ericksonienne</strong> est une méthode moderne, brève et scientifiquement documentée, particulièrement
            indiquée pour la gestion du stress, du sommeil ou de la confiance en soi.
            L'<strong className="text-sand-900 font-medium">hypnose spirituelle</strong> ouvre un espace
            d'exploration plus large, pour les personnes en questionnement de sens ou en transition intérieure.
          </p>
          <p>
            Une troisième notion — l'<strong className="text-sand-900 font-medium">hypnose régressive</strong> — est
            fréquemment recherchée. Chez Parfaits Imparfaits, elle constitue un outil intégré à la séance d'hypnose
            spirituelle, non une prestation distincte. Je vous l'explique en détail sur sa page dédiée.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-20" data-testid="hypnose-cards">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <InternalLinkCard
            to="/hypnose/ericksonienne"
            eyebrow="Approche 1"
            title="Hypnose ericksonienne"
            description="Une méthode douce, brève et scientifiquement reconnue pour agir sur le stress, l'anxiété, le sommeil, la confiance en soi et les blocages émotionnels."
            testid="card-ericksonienne"
          />
          <InternalLinkCard
            to="/hypnose/spirituelle"
            eyebrow="Approche 2"
            title="Hypnose spirituelle"
            description="Un espace d'exploration plus profond, pour les personnes en quête de sens, de cohérence ou de projection dans un futur aligné."
            testid="card-spirituelle"
          />
          <InternalLinkCard
            to="/hypnose/regressive"
            eyebrow="Point de repère"
            title="Hypnose régressive"
            description="Que recouvre ce terme ? Comment se distingue-t-elle des autres approches ? Une page pour clarifier — sans confusion."
            testid="card-regressive"
          />
          <InternalLinkCard
            to="/hypnose/questions-frequentes"
            eyebrow="Vos questions"
            title="Questions fréquentes"
            description="Perdre le contrôle, hypnotisabilité, contre-indications, séances à distance, tarifs, rythme… l'essentiel avant une première séance."
            testid="card-faq"
          />
        </div>
      </section>

      <EndCTA
        title="Une question, un projet, une envie de démarrer ?"
        text="Je réponds personnellement sous 24 h, par e-mail."
        testid="hypnose-end-cta"
      />
    </>
  );
}
